<template>
    <v-form ref="formRef" class="pb-4" data-cy="description-general-form">
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <div class="d-flex align-center">
                    <span class="text-h6 text--secondary">
                        {{ $t('CO2 Conversion Factors') }}
                    </span>
                    <v-tooltip location="top">
                        <template v-slot:activator="{props}">
                            <v-icon v-bind="props" size="small" class="ml-2" color="grey">
                                mdi-information-outline
                            </v-icon>
                        </template>
                        <span>{{ $t('co2_conversion_factors_tooltip') }}</span>
                    </v-tooltip>
                </div>
            </v-col>
            <v-col cols="12" md="8">
                <v-row dense>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="formData.conversionFactors.electricityConversion"
                            :label="$t('conversion_factors.electricity_label')"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="electricity-conversion-factor"
                            :rules="electricityValidationRules"
                            validate-on="input blur"
                            :readonly="
                                isHotelDescriptionMode && !app.$conf.public.account.allowToEditExternalHotelDescription
                            "
                            @update:model-value="clearApiError('electricityConversion')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="formData.conversionFactors.naturalGasConversion"
                            :label="$t('conversion_factors.natural_gas_label')"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="natural-gas-conversion-factor"
                            :rules="naturalGasValidationRules"
                            validate-on="input blur"
                            :readonly="
                                isHotelDescriptionMode && !app.$conf.public.account.allowToEditExternalHotelDescription
                            "
                            @update:model-value="clearApiError('naturalGasConversion')"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-text-field
                            v-model="formData.conversionFactors.oilConversion"
                            :label="$t('conversion_factors.oil_label')"
                            type="number"
                            hide-spin-buttons
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="oil-conversion-factor"
                            :rules="oilValidationRules"
                            validate-on="input blur"
                            :readonly="
                                isHotelDescriptionMode && !app.$conf.public.account.allowToEditExternalHotelDescription
                            "
                            @update:model-value="clearApiError('oilConversion')"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row v-if="classifications" dense class="mb-6">
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('filterTitles.classifications') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <v-row dense>
                    <v-col v-for="(group, index) in filteredClassifications" :key="index" cols="12" sm="3" md="2">
                        <div class="text-subtitle-1 font-weight-medium">{{ $t(`classifications.${index}`) }}:</div>
                        <div v-for="{id, name} in group" :key="id">
                            <v-checkbox
                                v-model="formData.classifications"
                                hide-details
                                :label="name"
                                :value="id"
                                density="compact"
                                :data-cy="id"
                                :readonly="
                                    isHotelDescriptionMode &&
                                    !app.$conf.public.account.allowToEditExternalHotelDescription
                                "
                            />
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn
                v-if="!isHotelDescriptionMode || app.$conf.public.account.allowToEditExternalHotelDescription"
                class="save-btn"
                :loading="isLoading"
                :disabled="isLoading"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading"
                :to="{name: 'supplierProducts'}"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {initIHotelGeneralDescription} from '~/utils/types/account/products/helpers'

    const POLICY_CLASSIFICATION_NAMES = ['Only Adults', 'Pets Friendly']

    const isLoading = ref<boolean>(false)
    const formRef = ref(null)

    const formData = ref({
        ...initIHotelGeneralDescription(),
        conversionFactors: {
            electricityConversion: 0.32,
            naturalGasConversion: 0.2,
            oilConversion: 0.27,
        },
    })

    const apiValidationErrors = ref({
        electricityConversion: null,
        naturalGasConversion: null,
        oilConversion: null,
    })

    const conversionFactorRules = [
        (value: any) => {
            if (value === null || value === undefined || value === '') return true
            const numValue = parseFloat(value)
            if (isNaN(numValue)) return t('validation.must_be_valid_number')
            if (numValue < 0) return t('validation.value_must_be_greater_than_or_equal_to_zero')
            return true
        },
    ]

    const electricityRangeRule = (value: any) => {
        if (value === null || value === undefined || value === '') return true
        const numValue = parseFloat(value)
        if (isNaN(numValue)) return true
        if (numValue < 0.26 || numValue > 0.38) {
            return t('validation.electricity_conversion_range')
        }
        return true
    }

    const naturalGasRangeRule = (value: any) => {
        if (value === null || value === undefined || value === '') return true
        const numValue = parseFloat(value)
        if (isNaN(numValue)) return true
        if (numValue < 0.2 || numValue > 0.21) {
            return t('validation.natural_gas_conversion_range')
        }
        return true
    }

    const oilRangeRule = (value: any) => {
        if (value === null || value === undefined || value === '') return true
        const numValue = parseFloat(value)
        if (isNaN(numValue)) return true
        if (numValue < 0.27 || numValue > 0.28) {
            return t('validation.oil_conversion_range')
        }
        return true
    }

    const electricityValidationRules = computed(() => [
        ...conversionFactorRules,
        electricityRangeRule,
        () => apiValidationErrors.value.electricityConversion || true,
    ])

    const naturalGasValidationRules = computed(() => [
        ...conversionFactorRules,
        naturalGasRangeRule,
        () => apiValidationErrors.value.naturalGasConversion || true,
    ])

    const oilValidationRules = computed(() => [
        ...conversionFactorRules,
        oilRangeRule,
        () => apiValidationErrors.value.oilConversion || true,
    ])

    const activeLangCode = ref<string>('en')
    provide('activeLangCode', activeLangCode)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const hotelDescriptionId: string = inject('hotelDescriptionId', null)
    const classifications = ref<Record<string, any[]> | null>(null)

    const sustainabilityItems = computed(() => {
        if (!classifications.value) return []
        const group = classifications.value['SUSTAINABILITY_PRACTICES'] || []
        return group.filter((item: any) => !POLICY_CLASSIFICATION_NAMES.some(name => item.name === name))
    })

    const sustainabilityIds = computed(() => new Set(sustainabilityItems.value.map((item: any) => item.id)))

    const filteredClassifications = computed(() => {
        if (!sustainabilityItems.value.length) return {}
        return {SUSTAINABILITY_PRACTICES: sustainabilityItems.value}
    })

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useHotelInfo, async () => {
        await loadData()
        loadHotelClassificationDictionaryInfo()
    })

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (supplierStore.fullContract?.hotelContentId || isHotelDescriptionMode) await loadData()
        loadHotelClassificationDictionaryInfo()
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reload)
    })

    async function reload(): Promise<void> {
        loadHotelClassificationDictionaryInfo()
    }

    async function loadData(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            isLoading.value = true
            const res = await app.$api.hotelDescriptionGeneral.get(hotelId, {
                locale: 'all',
            })
            let electricityValue =
                res.conversionFactors?.electricityConversion ?? formData.value.conversionFactors.electricityConversion
            let gasValue =
                res.conversionFactors?.naturalGasConversion ?? formData.value.conversionFactors.naturalGasConversion
            let oilValue = res.conversionFactors?.oilConversion ?? formData.value.conversionFactors.oilConversion

            if (gasValue === 0.27) gasValue = 0.2
            if (oilValue === 0.2) oilValue = 0.27

            formData.value = {
                ...formData.value,
                ...res,
                conversionFactors: {
                    electricityConversion: electricityValue,
                    naturalGasConversion: gasValue,
                    oilConversion: oilValue,
                },
            }
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadHotelClassificationDictionaryInfo(): Promise<void> {
        try {
            const res = await app.$api.hotelClassificationDictionaryInfo.get({
                supplierId: supplierStore.hotelInfo?.supplierId || 1,
            })
            const groupedArray = res.reduce((acc: Record<string, any[]>, curr: any) => {
                if (!acc[curr.groupCode]) {
                    acc[curr.groupCode] = []
                }
                acc[curr.groupCode].push(curr)
                return acc
            }, {})
            const desiredOrder = [
                'SUSTAINABILITY_PRACTICES',
                'POLICIES',
                'LOCATION',
                'BUDGET',
                'TARGET',
                'ARCHITECTURE',
                'OTHER',
            ]
            const keys = Object.keys(groupedArray)
            const sortedKeys = keys?.sort((a: string, b: string) => desiredOrder.indexOf(a) - desiredOrder.indexOf(b))
            const sortedObject: Record<string, any[]> = {}
            sortedKeys.forEach(key => {
                sortedObject[key] = groupedArray[key]?.sort((a: any, b: any) => a.name.localeCompare(b.name))
            })
            classifications.value = sortedObject
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        }
    }

    const clearApiError = (fieldName: string): void => {
        if (apiValidationErrors.value[fieldName]) {
            apiValidationErrors.value[fieldName] = null
        }
    }

    const mapApiErrorToField = (errorMessage: string): string | null => {
        if (!errorMessage) return null
        const lowerMessage = errorMessage.toLowerCase()
        if (lowerMessage.includes('electricity')) return 'electricityConversion'
        if (lowerMessage.includes('natural gas') || lowerMessage.includes('naturalgas')) return 'naturalGasConversion'
        if (lowerMessage.includes('oil')) return 'oilConversion'
        return null
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            apiValidationErrors.value = {
                electricityConversion: null,
                naturalGasConversion: null,
                oilConversion: null,
            }

            const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId

            // Re-fetch to get current non-sustainability classifications (e.g. policies saved from Details tab)
            const freshData = await app.$api.hotelDescriptionGeneral.get(hotelId, {locale: 'all'})
            const nonSustainabilityIds = (freshData.classifications || []).filter(
                (id: number) => !sustainabilityIds.value.has(id)
            )
            const sustainabilitySelections = (formData.value.classifications || []).filter((id: number) =>
                sustainabilityIds.value.has(id)
            )

            await app.$api.hotelDescriptionGeneral.put(hotelId, {
                ...formData.value,
                villaDescription: formData.value.villaDescription ?? {},
                classifications: [...nonSustainabilityIds, ...sustainabilitySelections],
            })

            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e: any) {
            let errors = null
            if (e?.response?.data?.errors && Array.isArray(e.response.data.errors)) {
                errors = e.response.data.errors
            } else if (e?.errors && Array.isArray(e.errors)) {
                errors = e.errors
            }

            if (errors) {
                const validationErrors = errors.filter((error: any) => error.errorType === 'validation')

                if (validationErrors.length > 0) {
                    let hasFieldErrors = false

                    validationErrors.forEach((error: any) => {
                        const errorMessage = error.message || error.token
                        const fieldName = mapApiErrorToField(errorMessage)

                        if (fieldName) {
                            apiValidationErrors.value[fieldName] = errorMessage
                            hasFieldErrors = true
                        }
                    })

                    if (hasFieldErrors) {
                        nextTick(() => {
                            if (formRef.value) {
                                formRef.value.validate()
                            }
                        })
                        return
                    }
                }
            }

            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }
</script>
